// Series data structure - supports multiple series
// Static fallback data - primary source is now API at /api/content/series
// This file is kept for offline/fallback support and type reference

export const allSeries = [
  {
    id: "it-leadership",
    slug: "it-leadership",
    name: "IT Leadership",
    fullName: "30-Day Leadership Skill Builder Series",
    tagline: "From Technical Expert to Confident Leader",
    description: "Transform from technical expert to confident IT leader in just 30 days. This practical guide delivers daily lessons combining leadership theory, real-world IT scenarios, and cutting-edge AI integration tips.",
    targetAudience: "IT Managers & Technical Leads",
    accentColor: "#DC2626",
    status: "active", // "active" | "coming-soon" | "draft"
    featured: true,
    purchaseLink: "#",
    author: {
      name: "Paul Hooper",
      bio: "Paul Hooper is a seasoned IT leadership expert with decades of experience guiding technical professionals on their journey to becoming confident leaders. His practical, actionable approach to leadership development draws from years of real-world IT management experience. Through the 30-Day Leadership Skill Builder Series, Paul shares his frameworks for building integrity, trust, and influence in the technology sector.",
      image: null
    },
    books: [
      {
        number: 1,
        title: "First Steps",
        subtitle: "Foundations of Effective IT Leadership",
        theme: "Lead Yourself",
        description: "Discover your principles and credibility. Build the foundation of authentic leadership.",
        status: "coming-soon",
        releaseDate: "2026-01-29",
        releaseDateDisplay: "Coming soon",
        color: "#DC2626",
        coverImage: "https://customer-assets.emergentagent.com/job_1149db47-69fd-49d0-a436-8d432b8e84b4/artifacts/2thw5zsx_30DS%20-%20B01%20-%20print%20-%20front.jpg",
        journeyDays: [
          {
            day: 1,
            week: 1,
            dayType: "weekday",
            title: "Why Integrity Is Your Leadership Cornerstone",
            theme: "Lead Yourself",
            description: "Discover why integrity forms the foundation of all effective leadership. Learn to align your actions with your values and build credibility from day one.",
            keyTakeaway: "Integrity isn't just about honesty—it's about consistency between your words, actions, and values.",
            icon: "Shield"
          },
          {
            day: 2,
            week: 1,
            dayType: "weekday",
            title: "Understanding Your Leadership Style",
            theme: "Lead Yourself",
            description: "Explore different leadership styles and identify your natural tendencies. Self-awareness is the first step to intentional growth.",
            keyTakeaway: "Knowing your default style helps you adapt to different situations and team needs.",
            icon: "User"
          },
          {
            day: 3,
            week: 1,
            dayType: "weekday",
            title: "Building Trust With Your Team",
            theme: "Lead Others",
            description: "Trust is earned through consistent actions over time. Learn the Trust Triangle framework and start building psychological safety.",
            keyTakeaway: "Trust = Credibility + Reliability + Intimacy, divided by Self-Orientation.",
            icon: "Users"
          },
          {
            day: 4,
            week: 1,
            dayType: "weekday",
            title: "The Power of Clear Communication",
            theme: "Lead Through Communication",
            description: "Master the art of clarity in your communications. From emails to team meetings, learn to convey your message with precision and impact.",
            keyTakeaway: "Clarity reduces confusion, builds confidence, and accelerates execution.",
            icon: "MessageSquare"
          },
          {
            day: 5,
            week: 1,
            dayType: "weekday",
            title: "Setting Expectations That Stick",
            theme: "Lead Systems",
            description: "Vague expectations lead to disappointing results. Learn the framework for setting clear, measurable, and achievable expectations.",
            keyTakeaway: "Great leaders make expectations explicit, not implicit.",
            icon: "Target"
          },
          {
            day: 6,
            week: 1,
            dayType: "weekend",
            title: "Week 1 Reflection & Integration",
            theme: "Weekend Reflection",
            description: "Pause to reflect on your first week's learnings. Integrate the concepts of integrity, self-awareness, trust, communication, and expectations into your leadership practice.",
            keyTakeaway: "Reflection transforms experience into insight. Take time to consolidate before moving forward.",
            icon: "Brain"
          }
        ]
      },
      {
        number: 2,
        title: "Stepping Up",
        subtitle: "Turning Peer Relationships into Leadership Success",
        theme: "Lead Others",
        description: "Navigate the challenging transition from peer to leader. Learn to build credibility, set boundaries, manage resistance, and create a unifying team narrative.",
        status: "coming-soon",
        releaseDate: "2026-03-25",
        releaseDateDisplay: "Late March 2026",
        color: "#3DD3EE",
        coverImage: "https://customer-assets.emergentagent.com/job_1149db47-69fd-49d0-a436-8d432b8e84b4/artifacts/1i7mr4sw_30DS%20-%20B02.jpg",
        journeyDays: [
          {
            day: 1,
            week: 1,
            dayType: "weekday",
            title: "Redefining Yourself: From Colleague to Leader",
            theme: "Transition",
            description: "The shift from being 'one of us' to 'one we follow.' Learn how to step up without stepping on toes, gain respect without losing authenticity, and balance empathy with accountability.",
            keyTakeaway: "Leadership doesn't change who you are — it reveals who you've become.",
            icon: "User"
          },
          {
            day: 2,
            week: 1,
            dayType: "weekday",
            title: "Building Credibility Without Distance",
            theme: "Credibility",
            description: "Establish credibility with your team while maintaining connection and approachability. Learn why credibility is demonstrated, not given, through consistent daily conduct.",
            keyTakeaway: "Credibility isn't given — it's demonstrated, day by day, through consistent conduct.",
            icon: "Award"
          },
          {
            day: 3,
            week: 1,
            dayType: "weekday",
            title: "Setting Boundaries Without Breaking Relationships",
            theme: "Boundaries",
            description: "Create strong, respectful boundaries that preserve trust while protecting your authority and wellbeing. Learn why boundaries are guide rails, not walls.",
            keyTakeaway: "Healthy boundaries protect relationships; unhealthy openness corrodes them.",
            icon: "Shield"
          },
          {
            day: 4,
            week: 1,
            dayType: "weekday",
            title: "Managing Resistance: Turning Doubt into Dialogue",
            theme: "Resistance",
            description: "Address pushback and skepticism from peers with confidence and emotional intelligence. Transform resistance from rebellion into information waiting to be heard.",
            keyTakeaway: "Resistance is not rebellion; it's information waiting to be heard.",
            icon: "MessageSquare"
          },
          {
            day: 5,
            week: 1,
            dayType: "weekday",
            title: "Establishing Your New Team Narrative",
            theme: "Vision",
            description: "Guide your team towards a shared story that reflects current realities and future aspirations. Create a clear, inclusive narrative that fosters shared purpose and identity.",
            keyTakeaway: "If you don't define your team's story, someone else will.",
            icon: "Target"
          },
          {
            day: 6,
            week: 1,
            dayType: "weekend",
            title: "Your Transition Map: From Peer to Purposeful Leader",
            theme: "Weekend Reflection",
            description: "Consolidate Week 1's learning by mapping your personal growth. Identify early wins, lessons learned, and areas for adjustment in your leadership transition.",
            keyTakeaway: "Growth always begins at the edge of discomfort.",
            icon: "Brain"
          }
        ]
      },
      {
        number: 3,
        title: "Clarity in Chaos",
        subtitle: "Mastering Organisation for IT Leaders",
        theme: "Lead Systems",
        description: "Build efficient systems for time management, team coordination, and operational excellence.",
        status: "coming-soon",
        releaseDate: "2026-05-15",
        releaseDateDisplay: "May 2026",
        color: "#B4DC19",
        coverImage: "https://customer-assets.emergentagent.com/job_1149db47-69fd-49d0-a436-8d432b8e84b4/artifacts/wmiot56p_30DS%20-%20B03.jpg"
      },
      {
        number: 4,
        title: "Leading with Integrity",
        subtitle: "Ethical Practices for IT Managers",
        theme: "Lead Ethically",
        description: "Navigate complex ethical decisions with confidence. Build a culture of transparency and accountability.",
        status: "coming-soon",
        color: "#FAD24B",
        coverImage: "https://customer-assets.emergentagent.com/job_41f2b231-df69-4121-9f07-63f0ed4adaf8/artifacts/81bfa9nm_30DS%20-%20B04%20-%20print%20-%20front.jpg"
      },
      {
        number: 5,
        title: "Communicating for Impact",
        subtitle: "The IT Manager's Guide to Effective Dialogue",
        theme: "Lead Through Communication",
        description: "Master the art of leadership communication. Connect your team to purpose through powerful dialogue.",
        status: "coming-soon",
        color: "#C8AFF0",
        coverImage: "https://customer-assets.emergentagent.com/job_41f2b231-df69-4121-9f07-63f0ed4adaf8/artifacts/wr9ljc82_30DS%20-%20B05%20-%20print%20-%20front.jpg"
      },
      {
        number: 6,
        title: "The Agile Leader",
        subtitle: "Adapting to Change in a Fast-Moving IT World",
        theme: "Lead with Agility",
        description: "Develop resilience and adaptability. Lead your team through uncertainty with confidence.",
        status: "coming-soon",
        color: "#FF8C19",
        coverImage: "https://customer-assets.emergentagent.com/job_41f2b231-df69-4121-9f07-63f0ed4adaf8/artifacts/v0pyafxs_30DS%20-%20B06%20-%20print%20-%20front.jpg"
      },
      {
        number: 7,
        title: "Coaching for Growth",
        subtitle: "Developing People and Potential",
        theme: "Lead Through Coaching",
        description: "Transform from manager to coach. Unlock your team's hidden potential through powerful coaching techniques.",
        status: "coming-soon",
        color: "#78286E",
        coverImage: "https://customer-assets.emergentagent.com/job_41f2b231-df69-4121-9f07-63f0ed4adaf8/artifacts/votwcm51_30DS%20-%20B07%20-%20print%20-%20front.jpg"
      },
      {
        number: 8,
        title: "The Strategic IT Leader",
        subtitle: "Linking Technology, Vision, and Business Value",
        theme: "Lead Strategically",
        description: "Expand your influence to the enterprise level. Drive organizational transformation and strategic initiatives.",
        status: "coming-soon",
        color: "#283750",
        coverImage: "https://customer-assets.emergentagent.com/job_41f2b231-df69-4121-9f07-63f0ed4adaf8/artifacts/euvs4quc_30DS%20-%20B08%20-%20print%20-%20front.jpg"
      }
    ],
    journeyDays: [
      {
        day: 1,
        week: 1,
        dayType: "weekday",
        title: "Why Integrity Is Your Leadership Cornerstone",
        theme: "Lead Yourself",
        description: "Discover why integrity forms the foundation of all effective leadership. Learn to align your actions with your values and build credibility from day one.",
        keyTakeaway: "Integrity isn't just about honesty—it's about consistency between your words, actions, and values.",
        icon: "Shield"
      },
      {
        day: 2,
        week: 1,
        dayType: "weekday",
        title: "Understanding Your Leadership Style",
        theme: "Lead Yourself",
        description: "Explore different leadership styles and identify your natural tendencies. Self-awareness is the first step to intentional growth.",
        keyTakeaway: "Knowing your default style helps you adapt to different situations and team needs.",
        icon: "User"
      },
      {
        day: 3,
        week: 1,
        dayType: "weekday",
        title: "Building Trust With Your Team",
        theme: "Lead Others",
        description: "Trust is earned through consistent actions over time. Learn the Trust Triangle framework and start building psychological safety.",
        keyTakeaway: "Trust = Credibility + Reliability + Intimacy, divided by Self-Orientation.",
        icon: "Users"
      },
      {
        day: 4,
        week: 1,
        dayType: "weekday",
        title: "The Power of Clear Communication",
        theme: "Lead Through Communication",
        description: "Master the art of clarity in your communications. From emails to team meetings, learn to convey your message with precision and impact.",
        keyTakeaway: "Clarity reduces confusion, builds confidence, and accelerates execution.",
        icon: "MessageSquare"
      },
      {
        day: 5,
        week: 1,
        dayType: "weekday",
        title: "Setting Expectations That Stick",
        theme: "Lead Systems",
        description: "Vague expectations lead to disappointing results. Learn the framework for setting clear, measurable, and achievable expectations.",
        keyTakeaway: "Great leaders make expectations explicit, not implicit.",
        icon: "Target"
      },
      {
        day: 6,
        week: 1,
        dayType: "weekend",
        title: "Week 1 Reflection & Integration",
        theme: "Weekend Reflection",
        description: "Pause to reflect on your first week's learnings. Integrate the concepts of integrity, self-awareness, trust, communication, and expectations into your leadership practice.",
        keyTakeaway: "Reflection transforms experience into insight. Take time to consolidate before moving forward.",
        icon: "Brain"
      }
    ],
    benefits: [
      {
        title: "30-Day Transformation",
        description: "Structured daily lessons that build on each other, creating lasting leadership habits.",
        icon: "Calendar"
      },
      {
        title: "IT-Specific Context",
        description: "Real scenarios from the technology sector that you'll recognize and can apply immediately.",
        icon: "Code"
      },
      {
        title: "AI-Powered Growth",
        description: "Modern integration tips showing how to leverage AI tools for leadership effectiveness.",
        icon: "Sparkles"
      },
      {
        title: "Weekend Reflections",
        description: "Dedicated reflection sessions each week to consolidate learning, integrate insights, and prepare for deeper growth.",
        icon: "Brain"
      }
    ]
  }
  // Future series will be added here
];

// Helper functions
export const getSeriesBySlug = (slug) => {
  return allSeries.find(s => s.slug === slug);
};

export const getActiveSeries = () => {
  return allSeries.filter(s => s.status === "active");
};

export const getFeaturedSeries = () => {
  return allSeries.filter(s => s.featured);
};

export const getDefaultSeries = () => {
  // Returns the first active series (for redirects)
  return getActiveSeries()[0];
};
